import React, { useState, useEffect, useRef } from "react";
import { Bot, Brain, Cpu, Globe, Search, BarChart, Users, Database, Code, Zap, Check, ArrowRight, Network, LayoutGrid, FileCode, AlertCircle, LineChart, Lightbulb } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";

// How many seconds each agent should be active
const AGENT_ACTIVE_DURATION = 14; // Each agent gets 14 seconds (7 agents = ~100 seconds total)
const TOTAL_ANALYSIS_DURATION = 75; // Extended to at least 60 seconds, extra time for completion animations

interface Agent {
  id: string;
  name: string;
  role: string;
  icon: JSX.Element;
  phase: number;
  messages: string[];
  subtasks: string[];
  color: string;
}

interface AITeamLoadingProps {
  progress: number;
  currentTask: string;
  manualControl?: boolean; // If true, uses the provided progress; if false, controls its own timing
  onComplete?: () => void;
  websiteUrl?: string; // Optional website URL to display
  forceComplete?: boolean; // If true, immediately show 100% and cleanup
}

export const AITeamLoading = ({ 
  progress: externalProgress,
  currentTask: externalTask,
  manualControl = false,
  onComplete,
  websiteUrl,
  forceComplete = false
}: AITeamLoadingProps) => {
  const [internalProgress, setInternalProgress] = useState(0);
  const [currentAgentIndex, setCurrentAgentIndex] = useState(0);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [currentSubtaskIndex, setCurrentSubtaskIndex] = useState(0);
  const [showPulse, setShowPulse] = useState(false);
  const [showSubtasks, setShowSubtasks] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const messageTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const subtaskTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pulseTimerRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const onCompleteFiredRef = useRef<boolean>(false);
  const isMountedRef = useRef(true);
  
  // Track if component is still mounted
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  
  // This is the progress we'll actually use (either provided or internal)
  const progress = manualControl ? externalProgress : internalProgress;
  
  // Create agents with more detailed message sequences
  const agents: Agent[] = [
    {
      id: "crawler",
      name: "Web Crawler Engine",
      role: "Structure Analyzer",
      icon: <Network className="h-5 w-5" />,
      phase: 14,
      color: "blue",
      messages: [
        "Initializing web crawler...",
        "Discovering website structure...",
        "Mapping navigation paths...",
        "Indexing page hierarchy...",
        "Analyzing internal linking structure...",
        "Cataloging content types...",
        "Evaluating site architecture..."
      ],
      subtasks: [
        "Scanning HTML document structure",
        "Extracting navigation menu items",
        "Mapping page relationships",
        "Identifying content hierarchy",
        "Analyzing URL structure patterns",
        "Detecting primary content sections",
        "Evaluating content organization logic"
      ]
    },
    {
      id: "content",
      name: "Content Analysis System",
      role: "Content Intelligence",
      icon: <FileCode className="h-5 w-5" />,
      phase: 28,
      color: "indigo",
      messages: [
        "Analyzing content quality...",
        "Extracting key topics...",
        "Identifying content themes...",
        "Evaluating messaging clarity...",
        "Checking content coherence...",
        "Analyzing tone and voice...",
        "Mapping content to business goals..."
      ],
      subtasks: [
        "Calculating readability scores",
        "Extracting primary topics and themes",
        "Identifying key message patterns",
        "Analyzing content tone consistency",
        "Evaluating clarity of value proposition",
        "Mapping content against industry benchmarks",
        "Detecting content gaps and opportunities"
      ]
    },
    {
      id: "seo",
      name: "SEO Analysis System",
      role: "Search Optimization Specialist",
      icon: <Search className="h-5 w-5" />,
      phase: 42,
      color: "violet",
      messages: [
        "Scanning meta information...",
        "Analyzing content relevance...",
        "Evaluating keyword density...",
        "Checking heading structures...",
        "Assessing content quality...",
        "Identifying semantic relationships...",
        "Analyzing competitor SEO strategies..."
      ],
      subtasks: [
        "Extracting title tags and meta descriptions",
        "Analyzing keyword placement and density",
        "Evaluating heading structure hierarchy",
        "Checking image alt text optimization",
        "Assessing internal linking patterns",
        "Detecting potential ranking factors",
        "Identifying SEO improvement opportunities"
      ]
    },
    {
      id: "audience",
      name: "Audience Intelligence",
      role: "User Persona Identifier",
      icon: <Users className="h-5 w-5" />,
      phase: 56, 
      color: "emerald",
      messages: [
        "Identifying target demographics...",
        "Analyzing audience interests...",
        "Mapping user journey paths...",
        "Assessing engagement patterns...",
        "Creating user personas...",
        "Identifying customer pain points...",
        "Analyzing user motivation factors..."
      ],
      subtasks: [
        "Detecting audience demographic indicators",
        "Mapping customer journey touchpoints",
        "Identifying potential pain points",
        "Analyzing messaging-to-audience alignment",
        "Extracting key audience motivations",
        "Evaluating user experience factors",
        "Creating audience segment profiles"
      ]
    },
    {
      id: "competitor",
      name: "Market Position Analyzer",
      role: "Competitive Intelligence",
      icon: <BarChart className="h-5 w-5" />,
      phase: 70,
      color: "amber",
      messages: [
        "Identifying market competitors...",
        "Analyzing competitor positioning...",
        "Evaluating feature differentiation...",
        "Assessing pricing strategies...",
        "Measuring competitive advantage...",
        "Identifying market gaps...",
        "Calculating market position score..."
      ],
      subtasks: [
        "Detecting primary market competitors",
        "Analyzing competitive positioning language",
        "Mapping feature differentiation factors",
        "Evaluating unique selling propositions",
        "Identifying market opportunity gaps",
        "Detecting competitor messaging patterns",
        "Scoring market position strength"
      ]
    },
    {
      id: "technical",
      name: "Technical Performance Engine",
      role: "Architecture Evaluator",
      icon: <Code className="h-5 w-5" />,
      phase: 84,
      color: "rose",
      messages: [
        "Analyzing website architecture...",
        "Evaluating technology stack...",
        "Measuring page load performance...",
        "Checking responsive design...",
        "Assessing accessibility compliance...",
        "Testing cross-browser compatibility...",
        "Compiling technical recommendations..."
      ],
      subtasks: [
        "Detecting technology stack signatures",
        "Analyzing page performance indicators",
        "Evaluating responsive design implementation",
        "Checking accessibility compliance factors",
        "Identifying technical optimization opportunities",
        "Mapping site architecture patterns",
        "Scoring technical implementation quality"
      ]
    },
    {
      id: "insights",
      name: "Growth Insight Generator",
      role: "Strategic Advisor",
      icon: <Lightbulb className="h-5 w-5" />,
      phase: 100,
      color: "cyan",
      messages: [
        "Generating strategic insights...",
        "Identifying growth opportunities...",
        "Prioritizing action recommendations...",
        "Calculating potential impact scores...",
        "Creating content opportunity map...",
        "Compiling growth strategy blueprint...",
        "Finalizing personalized recommendations..."
      ],
      subtasks: [
        "Synthesizing cross-domain analysis data",
        "Calculating opportunity impact potential",
        "Prioritizing recommendations by ROI",
        "Creating audience-aligned content plan", 
        "Mapping competitive positioning strategy",
        "Developing technical optimization roadmap",
        "Finalizing comprehensive growth blueprint"
      ]
    }
  ];

  // Get the current agent based on progress
  const getCurrentAgent = () => {
    for (let i = agents.length - 1; i >= 0; i--) {
      if (progress >= (i * (100 / agents.length))) {
        return i;
      }
    }
    return 0;
  };

  // Get current message for the active agent
  const getCurrentTaskMessage = () => {
    const agentIndex = getCurrentAgent();
    return agents[agentIndex].messages[currentMessageIndex % agents[agentIndex].messages.length];
  };

  // Control the progress internally if not using manual control
  useEffect(() => {
    if (manualControl) return;

    // Start the timer on first render
    if (startTimeRef.current === null) {
      startTimeRef.current = Date.now();
    }

    // Reset the onComplete fired flag
    onCompleteFiredRef.current = false;

    // Calculate and update progress every 100ms
    const timer = setInterval(() => {
      if (startTimeRef.current === null || !isMountedRef.current) return;
      
      const elapsedTime = (Date.now() - startTimeRef.current) / 1000; // in seconds
      
      // For progress over TOTAL_ANALYSIS_DURATION seconds:
      // - Keep the progress at 99% so the loading state continues
      // - Keep showing the last agent as active
      // - We'll only move to 100% when the actual API call completes
      let newProgress;
      if (elapsedTime >= TOTAL_ANALYSIS_DURATION) {
        newProgress = 99; // Keep at 99% to maintain loading state
      } else {
        newProgress = Math.min(99, (elapsedTime / TOTAL_ANALYSIS_DURATION) * 100);
      }
      
      // Check if we should move to the next agent
      let newAgentIndex;
      if (elapsedTime >= TOTAL_ANALYSIS_DURATION) {
        // If we're past total duration, stay on the last agent
        newAgentIndex = agents.length - 1;
      } else {
        // Calculate which agent should be active based on elapsed time
        newAgentIndex = Math.min(
          Math.floor((newProgress / 100) * agents.length),
          agents.length - 1
        );
      }
      
      if (newAgentIndex !== currentAgentIndex && isMountedRef.current) {
        setCurrentAgentIndex(newAgentIndex);
        setCurrentMessageIndex(0); // Reset message index for new agent
        setCurrentSubtaskIndex(0); // Reset subtask index for new agent
        setShowPulse(true);
        setShowSubtasks(true); // Show subtasks when a new agent becomes active
        
        // Use RAF for smoother animation on slower devices
        if (pulseTimerRef.current) {
          cancelAnimationFrame(pulseTimerRef.current);
        }
        
        pulseTimerRef.current = requestAnimationFrame(() => {
          const pulseTimeout = setTimeout(() => {
            if (isMountedRef.current) {
              setShowPulse(false);
            }
            pulseTimerRef.current = null;
          }, 1000);
          
          // Store the timeout ID in the closure so we can clean it up later
          return () => clearTimeout(pulseTimeout);
        });
      }
      
      if (isMountedRef.current) {
        setInternalProgress(newProgress);
      }
      
      // Only trigger onComplete after TOTAL_ANALYSIS_DURATION has passed
      // We no longer stop the timer - let it continue to show activity
      if (elapsedTime >= TOTAL_ANALYSIS_DURATION && onComplete && !onCompleteFiredRef.current) {
        // Use a ref to prevent calling onComplete multiple times
        onCompleteFiredRef.current = true;
        onComplete();
        // Note: we don't clear the interval - let it keep running to show animation
      }
    }, 100);
    
    timerRef.current = timer;
    
    // Use a more conservative interval for message changes
    const updateMessage = () => {
      if (!isMountedRef.current) return;
      
      setCurrentMessageIndex((prev) => {
        const currentAgent = agents[currentAgentIndex];
        return (prev + 1) % currentAgent.messages.length;
      });
      
      // Only show subtasks sometimes, for visual interest
      setShowSubtasks(Math.random() > 0.3);
      
      messageTimerRef.current = setTimeout(updateMessage, 3000);
    };
    
    messageTimerRef.current = setTimeout(updateMessage, 3000);
    
    // Add a separate timer for updating subtasks at a different rate
    const updateSubtask = () => {
      if (!isMountedRef.current) return;
      
      setCurrentSubtaskIndex((prev) => {
        const currentAgent = agents[currentAgentIndex];
        return (prev + 1) % currentAgent.subtasks.length;
      });
      
      subtaskTimerRef.current = setTimeout(updateSubtask, 2000);
    };
    
    subtaskTimerRef.current = setTimeout(updateSubtask, 2000);
    
    // Proper cleanup of all timers
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      if (messageTimerRef.current) {
        clearTimeout(messageTimerRef.current);
        messageTimerRef.current = null;
      }
      
      if (subtaskTimerRef.current) {
        clearTimeout(subtaskTimerRef.current);
        subtaskTimerRef.current = null;
      }
      
      if (pulseTimerRef.current) {
        cancelAnimationFrame(pulseTimerRef.current);
        pulseTimerRef.current = null;
      }
    };
  }, [manualControl, currentAgentIndex, onComplete, agents]);

  // Immediately jump to 100% if forceComplete is true
  useEffect(() => {
    if (forceComplete && !manualControl && isMountedRef.current) {
      // Jump to 100% progress
      setInternalProgress(100);
      
      // Mark the last agent as complete
      setCurrentAgentIndex(agents.length - 1);
      
      // Trigger onComplete if it exists
      if (onComplete && !onCompleteFiredRef.current) {
        onCompleteFiredRef.current = true;
        onComplete();
      }
      
      // Clean up timers
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      if (messageTimerRef.current) {
        clearTimeout(messageTimerRef.current);
        messageTimerRef.current = null;
      }
      
      if (subtaskTimerRef.current) {
        clearTimeout(subtaskTimerRef.current);
        subtaskTimerRef.current = null;
      }
      
      if (pulseTimerRef.current) {
        cancelAnimationFrame(pulseTimerRef.current);
        pulseTimerRef.current = null;
      }
    }
  }, [forceComplete, onComplete, manualControl, agents.length]);

  // Handle component unmounting cleanly
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      if (messageTimerRef.current) {
        clearTimeout(messageTimerRef.current);
        messageTimerRef.current = null;
      }
      
      if (subtaskTimerRef.current) {
        clearTimeout(subtaskTimerRef.current);
        subtaskTimerRef.current = null;
      }
      
      if (pulseTimerRef.current) {
        cancelAnimationFrame(pulseTimerRef.current);
        pulseTimerRef.current = null;
      }
      
      startTimeRef.current = null;
      onCompleteFiredRef.current = false;
      isMountedRef.current = false;
    };
  }, []);

  // Current status message to display
  const currentTask = manualControl ? externalTask : getCurrentTaskMessage();
  const currentAgent = agents[currentAgentIndex];
  const currentSubtask = currentAgent.subtasks[currentSubtaskIndex % currentAgent.subtasks.length];

  // Helper to get color classes based on agent color
  const getColorClasses = (agent: Agent, type: 'bg' | 'text' | 'border') => {
    const colorMap: Record<string, Record<string, string>> = {
      blue: {
        bg: 'bg-blue-100 dark:bg-blue-900/30',
        text: 'text-blue-700 dark:text-blue-400',
        border: 'border-blue-200 dark:border-blue-800/30'
      },
      indigo: {
        bg: 'bg-blue-100 dark:bg-blue-900/30',
        text: 'text-blue-700 dark:text-blue-400',
        border: 'border-blue-200 dark:border-blue-800/30'
      },
      violet: {
        bg: 'bg-violet-100 dark:bg-violet-900/30',
        text: 'text-violet-700 dark:text-violet-400',
        border: 'border-violet-200 dark:border-violet-800/30'
      },
      emerald: {
        bg: 'bg-emerald-100 dark:bg-emerald-900/30',
        text: 'text-emerald-700 dark:text-emerald-400',
        border: 'border-emerald-200 dark:border-emerald-800/30'
      },
      amber: {
        bg: 'bg-amber-100 dark:bg-amber-900/30',
        text: 'text-amber-700 dark:text-amber-400',
        border: 'border-amber-200 dark:border-amber-800/30'
      },
      rose: {
        bg: 'bg-rose-100 dark:bg-rose-900/30',
        text: 'text-rose-700 dark:text-rose-400',
        border: 'border-rose-200 dark:border-rose-800/30'
      },
      cyan: {
        bg: 'bg-cyan-100 dark:bg-cyan-900/30',
        text: 'text-cyan-700 dark:text-cyan-400',
        border: 'border-cyan-200 dark:border-cyan-800/30'
      },
      green: {
        bg: 'bg-green-100 dark:bg-green-900/30',
        text: 'text-green-700 dark:text-green-400',
        border: 'border-green-200 dark:border-green-800/30'
      }
    };

    return colorMap[agent.color]?.[type] || colorMap.blue[type];
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-4">
        <h2 className="text-3xl font-bold gradient-text">
          Website Analysis in Progress
        </h2>
        <p className="text-lg text-muted-foreground">
          Our AI agents are analyzing your website data
        </p>
        
        {websiteUrl && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-full text-sm"
          >
            <Globe className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="font-medium truncate max-w-[200px] sm:max-w-[250px]">
              {websiteUrl.replace(/^https?:\/\//i, '')}
            </span>
          </motion.div>
        )}
      </div>

      {/* Progress Bar with Enhanced UI - Moved higher up for better visibility */}
      <div className="space-y-3 max-w-xl mx-auto mb-2">
        <div className="flex justify-between items-center">
          <div className={cn(
            "text-sm font-semibold flex items-center gap-2",
            getColorClasses(currentAgent, 'text')
          )}>
            <span>Analysis Progress</span>
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-1.5 h-1.5 rounded-full bg-current"
            />
          </div>
          <div className={cn(
            "text-sm font-bold",
            getColorClasses(currentAgent, 'text')
          )}>
            {Math.round(progress)}%
          </div>
        </div>
        
        <div className="relative h-2.5 bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
          <motion.div 
            className="h-full rounded-full"
            style={{
              background: `linear-gradient(to right, var(--${currentAgent.color}-500), var(--${currentAgent.color}-400))`
            }}
            initial={{ width: "0%" }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5 }}
          />
          
          {/* Animated pulse along the progress bar */}
          {progress < 100 && (
            <motion.div 
              className="absolute top-0 bottom-0 w-8 bg-white/40 dark:bg-white/10 rounded-full blur-sm"
              style={{ left: `${progress - 4}%` }}
              animate={{
                opacity: [0, 1, 0],
              }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          )}
        </div>
        
        <p className="text-sm text-zinc-500 dark:text-zinc-400 text-center italic">
          {progress < 95 ? "Estimated completion time: ~1 minute" : "Almost finished..."}
        </p>
        
        {!manualControl && (
          <button 
            onClick={() => {
              if (onComplete && !onCompleteFiredRef.current) {
                onCompleteFiredRef.current = true;
                onComplete();
              }
            }}
            className="mt-1 text-xs text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors mx-auto block"
            aria-label="Skip animation"
          >
            Skip
          </button>
        )}
      </div>

      {/* Active Agent Highlight Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={cn(
          "relative p-4 rounded-lg border shadow-md mb-4 overflow-hidden max-w-xl mx-auto",
          getColorClasses(currentAgent, 'border'),
          getColorClasses(currentAgent, 'bg')
        )}
      >
        <div className="flex items-start gap-4">
          <div className={cn(
            "p-2 rounded-md bg-white dark:bg-zinc-800",
            getColorClasses(currentAgent, 'text')
          )}>
            <motion.div
              animate={{ 
                scale: [1, 1.1, 1],
                opacity: [1, 0.8, 1] 
              }}
              transition={{ 
                repeat: Infinity,
                repeatType: "loop",
                duration: 2,
                ease: "easeInOut" 
              }}
            >
              {currentAgent.icon}
            </motion.div>
          </div>
          
          <div className="flex-1">
            <div className="flex justify-between items-start">
              <div>
                <h3 className={cn(
                  "font-semibold",
                  getColorClasses(currentAgent, 'text')
                )}>
                  {currentAgent.name}
                </h3>
                <p className="text-xs text-zinc-600 dark:text-zinc-400">
                  {currentAgent.role}
                </p>
              </div>
              <div className="text-xs px-2 py-1 rounded-full bg-white/50 dark:bg-zinc-800/50 text-zinc-600 dark:text-zinc-300">
                Phase {currentAgentIndex + 1}/{agents.length}
              </div>
            </div>
            
            <div className="mt-3">
              <div className={cn(
                "text-sm mb-2 font-medium",
                getColorClasses(currentAgent, 'text')
              )}>
                <motion.span
                  animate={{ opacity: [1, 0.7, 1] }}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                >
                  {currentTask || `Processing ${currentAgent.name.toLowerCase()}...`}
                </motion.span>
              </div>
              
              {showSubtasks && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  transition={{ duration: 0.3 }}
                  className="bg-white/60 dark:bg-zinc-800/60 p-2 rounded text-xs text-zinc-700 dark:text-zinc-300"
                >
                  <div className="flex gap-2 items-center">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                      className="h-3 w-3 border-2 border-current border-t-transparent rounded-full opacity-70"
                    />
                    <span>{currentSubtask}</span>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
        
        {/* Animated pulse background effect */}
        <motion.div
          className="absolute inset-0 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ 
            opacity: [0, 0.1, 0],
            scale: [0.9, 1.05, 0.9]
          }}
          transition={{ 
            duration: 4, 
            repeat: Infinity, 
            repeatType: "loop",
          }}
        />
      </motion.div>

      {/* Agent Activity Cards - Grid layout for better use of space */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 gap-y-3 max-w-6xl mx-auto">
        {agents.map((agent, index) => {
          const isActive = index === currentAgentIndex;
          const isComplete = progress >= agent.phase;
          const isWaiting = index > currentAgentIndex;
          
          return (
            <motion.div 
              key={agent.id}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className={cn(
                "transition-all duration-300",
                isActive && "scale-[1.02] z-10"
              )}
            >
              <Card 
                className={cn(
                  "overflow-hidden transition-all duration-300",
                  isActive ? "border-" + agent.color + "-300 dark:border-" + agent.color + "-700 shadow-lg ring-1 ring-" + agent.color + "-200 dark:ring-" + agent.color + "-800/30" : 
                  isComplete ? "border-green-300 dark:border-green-700" : 
                  "border-zinc-200 dark:border-zinc-700"
                )}
              >
                <CardContent className="p-3">
                  <div className="flex items-start gap-2">
                    <div className={cn(
                      "p-1.5 rounded-md",
                      isComplete 
                        ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400" 
                        : isActive
                          ? "bg-white/80 dark:bg-zinc-900/80 " + getColorClasses(agent, 'text')
                          : "bg-zinc-100 dark:bg-zinc-700 text-zinc-500 dark:text-zinc-400"
                    )}>
                      {isComplete ? (
                        <Check className="h-4 w-4" />
                      ) : isActive ? (
                        <motion.div
                          animate={{ 
                            scale: [1, 1.2, 1],
                            opacity: [1, 0.8, 1] 
                          }}
                          transition={{ 
                            repeat: Infinity,
                            repeatType: "loop",
                            duration: 2,
                            ease: "easeInOut" 
                          }}
                        >
                          {agent.icon}
                        </motion.div>
                      ) : (
                        agent.icon
                      )}
                    </div>
                    <div className="flex-1 space-y-0.5">
                      <div className="flex items-center justify-between">
                        <h3 className={cn(
                          "font-medium text-sm truncate max-w-[170px]",
                          isActive 
                            ? getColorClasses(agent, 'text')
                            : isComplete
                              ? "text-green-700 dark:text-green-400"
                              : "text-zinc-500 dark:text-zinc-400"
                        )}>
                          {agent.name}
                        </h3>
                      </div>
                      
                      <div className="min-h-[1.5rem] relative">
                        {isActive && (
                          <motion.div 
                            className={cn(
                              "absolute left-0 h-full w-1 rounded-full",
                              getColorClasses(agent, 'bg')
                            )}
                            layoutId="activeIndicator"
                            transition={{ type: "spring", stiffness: 300, damping: 30 }}
                          />
                        )}
                        
                        <p className={cn(
                          "text-xs pl-2 line-clamp-2",
                          isActive 
                            ? getColorClasses(agent, 'text')
                            : isComplete
                              ? "text-green-600 dark:text-green-400"
                              : "text-zinc-500 dark:text-zinc-400"
                        )}>
                          {isComplete ? (
                            <span className="flex items-center">
                              Complete <Check className="ml-1 h-3 w-3" />
                            </span>
                          ) : isActive ? (
                            <span>
                              {currentTask || `Processing ${agent.name.toLowerCase()}...`}
                              <motion.span
                                animate={{ opacity: [0, 1, 0] }}
                                transition={{ repeat: Infinity, duration: 1.5 }}
                                className="inline-block ml-1"
                              >
                                _
                              </motion.span>
                            </span>
                          ) : (
                            <span className="opacity-60">Waiting...</span>
                          )}
                        </p>
                      </div>
                      
                      {isActive && (
                        <div className="w-full mt-1">
                          <motion.div
                            className={cn(
                              "h-0.5 rounded-full",
                              getColorClasses(agent, 'bg')
                            )}
                            initial={{ width: "0%" }}
                            animate={{ width: "100%" }}
                            transition={{ 
                              duration: 3,
                              repeat: Infinity,
                              repeatType: "loop",
                              ease: "easeInOut"
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

